import React, { useContext } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    Stack,
    CircularProgress,
    Alert,
    IconButton,
    Divider,
    useTheme,
    useMediaQuery
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import InfoIcon from "@mui/icons-material/Info";
import InventoryIcon from "@mui/icons-material/Inventory";
import { PETICION_ESTADO } from "../../constants";
import EstadoBadge from "../ui/EstadoBadge";
import { StateContext } from "../../../../components/context/StateProvider";
import StockDisplay from "../ui/StockDisplay";

/**
 * Modal de confirmación para traspasar una petición a otros almacenes
 */
export default function ModalTraspasar({ show, onHide, peticion, onConfirm, loading }) {
    const { getNombreAlmacen } = useContext(StateContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <Dialog
            open={show}
            onClose={loading ? undefined : onHide}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    m: isMobile ? 0 : 2,
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: "#fff3cd",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <CompareArrowsIcon sx={{ color: "#ffc107", fontSize: { xs: 20, sm: 24 } }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: "#212529",
                            fontSize: { xs: "1.1rem", sm: "1.25rem" },
                        }}
                    >
                        {isMobile ? "Traspasar" : "Confirmar Traspaso"}
                    </Typography>
                </Box>
                <IconButton
                    onClick={onHide}
                    disabled={loading}
                    size="small"
                    sx={{
                        color: "#6c757d",
                        "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.04)",
                        },
                    }}
                >
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            {/* Content */}
            <DialogContent
                sx={{
                    pt: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    flex: isMobile ? 1 : "initial",
                }}
            >
                {peticion && (
                    <Stack spacing={{ xs: 2, sm: 2.5 }}>
                        {/* Mensaje de confirmación */}
                        <Typography
                            sx={{
                                fontSize: { xs: 14, sm: 15 },
                                color: "#495057",
                                lineHeight: 1.6,
                            }}
                        >
                            ¿Está seguro de que desea <strong style={{ color: "#ffc107" }}>traspasar</strong> esta
                            petición a otros almacenes con stock?
                        </Typography>

                        {/* Información de la petición */}
                        <Box
                            sx={{
                                p: { xs: 1.5, sm: 2 },
                                backgroundColor: "#f8f9fa",
                                borderRadius: 2,
                                border: "1px solid #e9ecef",
                            }}
                        >
                            <Stack spacing={{ xs: 1.25, sm: 1.5 }}>
                                {/* Referencia */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 0.5, sm: 1 },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 11, sm: 12 },
                                            fontWeight: 600,
                                            color: "#6c757d",
                                            minWidth: { xs: "auto", sm: 80 },
                                        }}
                                    >
                                        Referencia:
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 13, sm: 14 },
                                            fontWeight: 700,
                                            color: "#212529",
                                            letterSpacing: "0.3px",
                                        }}
                                    >
                                        {peticion.referencia || "-"}
                                    </Typography>
                                </Box>

                                {/* Producto */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "flex-start" },
                                        gap: { xs: 0.5, sm: 1 },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 11, sm: 12 },
                                            fontWeight: 600,
                                            color: "#6c757d",
                                            minWidth: { xs: "auto", sm: 80 },
                                        }}
                                    >
                                        Producto:
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 13, sm: 14 },
                                            color: "#495057",
                                            flex: 1,
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {peticion.nombreProducto || "-"}
                                    </Typography>
                                </Box>

                                {/* Unidades */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 0.5, sm: 1 },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 11, sm: 12 },
                                            fontWeight: 600,
                                            color: "#6c757d",
                                            minWidth: { xs: "auto", sm: 80 },
                                        }}
                                    >
                                        Unidades:
                                    </Typography>
                                    <Chip
                                        icon={<InventoryIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                                        label={peticion.unidades || 0}
                                        size="small"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: { xs: 11, sm: 12 },
                                            height: { xs: 24, sm: 28 },
                                            backgroundColor: "#fff",
                                            border: "1px solid #dee2e6",
                                        }}
                                    />
                                </Box>

                                {/* Estado */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 0.5, sm: 1 },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 11, sm: 12 },
                                            fontWeight: 600,
                                            color: "#6c757d",
                                            minWidth: { xs: "auto", sm: 80 },
                                        }}
                                    >
                                        Estado:
                                    </Typography>
                                    <EstadoBadge estado={peticion.estado} />
                                </Box>

                                {/* Almacenes asignados */}
                                {peticion.almacenesAsignados && peticion.almacenesAsignados.length > 0 && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: { xs: "column", sm: "row" },
                                            alignItems: { xs: "flex-start", sm: "flex-start" },
                                            gap: { xs: 0.5, sm: 1 },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 11, sm: 12 },
                                                fontWeight: 600,
                                                color: "#6c757d",
                                                minWidth: { xs: "auto", sm: 80 },
                                            }}
                                        >
                                            Almacenes:
                                        </Typography>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                            {peticion.almacenesAsignados.map((almacen) => (
                                                <Chip
                                                    key={almacen}
                                                    label={getNombreAlmacen(almacen) || almacen}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: { xs: 10, sm: 11 },
                                                        height: { xs: 22, sm: 24 },
                                                        backgroundColor: "#fff",
                                                        border: "1px solid #dee2e6",
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Stack>
                        </Box>

                        {/* Stock disponible */}
                        {peticion.stock && peticion.stock.length > 0 && (
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: { xs: 11, sm: 12 },
                                        fontWeight: 600,
                                        color: "#6c757d",
                                        mb: 1,
                                    }}
                                >
                                    Stock disponible:
                                </Typography>
                                <StockDisplay
                                    stock={peticion.stock}
                                    getNombreAlmacen={getNombreAlmacen}
                                    variant="expanded"
                                />
                            </Box>
                        )}

                        {/* Alerta informativa */}
                        <Alert
                            severity="warning"
                            icon={<InfoIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                            sx={{
                                backgroundColor: "#fff3cd",
                                color: "#856404",
                                border: "1px solid #ffc107",
                                display: "flex",
                                alignItems: "center",
                                "& .MuiAlert-icon": {
                                    color: "#ffc107",
                                    alignItems: "center",
                                },
                                "& .MuiAlert-message": {
                                    display: "flex",
                                    alignItems: "center",
                                    py: { xs: 0.75, sm: 1 },
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: { xs: 12, sm: 13 },
                                    fontWeight: 600,
                                }}
                            >
                                La petición se reasignará a otros almacenes con stock disponible
                            </Typography>
                        </Alert>
                    </Stack>
                )}
            </DialogContent>

            <Divider />

            {/* Actions */}
            <DialogActions
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    gap: { xs: 1, sm: 1.5 },
                    flexDirection: { xs: "column-reverse", sm: "row" },
                }}
            >
                <Button
                    onClick={onHide}
                    disabled={loading}
                    variant="outlined"
                    fullWidth={isMobile}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        color: "#6c757d",
                        borderColor: "#6c757d",
                        "&:hover": {
                            borderColor: "#495057",
                            backgroundColor: "rgba(0,0,0,0.04)",
                        },
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={onConfirm}
                    disabled={loading}
                    variant="contained"
                    fullWidth={isMobile}
                    startIcon={
                        loading ? (
                            <CircularProgress size={16} sx={{ color: "#fff" }} />
                        ) : (
                            <CompareArrowsIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        )
                    }
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        backgroundColor: "#ffc107",
                        color: "#212529",
                        "&:hover": {
                            backgroundColor: "#e0a800",
                        },
                        "&:disabled": {
                            backgroundColor: "#6c757d",
                            color: "#fff",
                        },
                    }}
                >
                    {loading ? "Traspasando..." : isMobile ? "Traspasar" : "Confirmar Traspaso"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
